import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import MoreVert from '@material-ui/icons/MoreVert';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Media from 'react-media';
import materialTheme from '../reactMaterialTheme';
import Mixpanel from '../shared/Mixpanel';
import http from '../shared/http';
import AdOptionsDrawer from './AdOptionsDrawer';
import AdOptionsDropdown from './AdOptionsDropdown';
import { useCurrentUser } from '../hooks';

const CreativeOptions = props => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [editLink, setEditLink] = useState(null);
  const [embedLink, setEmbedLink] = useState(null);
  const [viewLink, setViewLink] = useState(null);
  const currentUser = useCurrentUser();
  const isOpen = () => Boolean(anchorEl);

  const handleIconClick = event => {
    Mixpanel.track('Opened Creative Options Menu List');

    setViewLink(props.ShareableLinkService.buildShareUrl(props.creative.id));

    if (props.adType === 'demo') {
      setEditLink(props.ShareableLinkService.buildEditUrl(props.creative.id));
    }

    /* Embed URL changes with publisher-select dropdown, so
     * regenerate it every time the menu is shown */
    setAnchorEl(event.currentTarget);
    setEmbedLink(props.ShareableLinkService.buildEmbedUrl(props.creative));
  };

  const hideMenu = () => {
    setAnchorEl(null);
    setEmbedLink(null);
  };

  const handleViewContent = () => {
    Mixpanel.track('Clicked to View Creative', {
      id: props.creative.id
    });

    return props.$state.go('creativePreview.saved', {
      id: props.creative.id
    });
  };

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this creative?")) {
      return;
    }

    Mixpanel.track('Clicked to Delete Creative', {
      id: props.creative.id
    });

    await http.delete(`/creative_previews/${props.creative.id}`);
    flashMessage('Creative Successfully Deleted');
    props.CreativeListService.refreshList();
    hideMenu();
  };

  const handleEditAd = () => {
    Mixpanel.track('Clicked to Edit Creative', {
      id: props.creative.id
    });

    return props.$state.go('creativePreview.edit.saved', {
      id: props.creative.id
    });
  };

  const copyViewLink = () => {
    Mixpanel.track('Copied View Link', {
      id: props.creative.id
    });

    flashMessage('View Link Copied to Clipboard');
    hideMenu();
  };

  const copyEditLink = () => {
    Mixpanel.track('Copied Edit Link', {
      id: props.creative.id
    });

    flashMessage('Edit Link Copied to Clipboard');
    hideMenu();
  };

  const copyEmbedLink = () => {
    Mixpanel.track('Copied Embed Link', {
      id: props.creative.id
    });

    flashMessage('Embed Link Copied to Clipboard');
    hideMenu();
  };

  const copyIntegrationLink = () => {
    flashMessage('Integration Link Copied to Clipboard');
    hideMenu();
  };

  const flashMessage = message => {
    props.FlashService.push('success', message);
  };

  const integrationLink = () => {
    if (currentUser && currentUser.superUser) {
      return `https://integration.sharethrough.com/tools/sfp-tag-simulator/49e170a8?str_modify=true&nag=${
        props.creative.id
      }`;
    }
  };

  const commonProps = {
    handleViewContent,
    handleEditAd,
    viewLink,
    copyViewLink,
    editLink,
    copyEditLink,
    embedLink,
    copyEmbedLink,
    integrationLink: integrationLink(),
    copyIntegrationLink,
    adType: props.adType,
    handleDelete
  };

  return (
    <MuiThemeProvider theme={materialTheme}>
      <div className="dropdown inline ad-options">
        <button
          aria-label="Actions"
          className="dropdown-button ad-options-button"
          onClick={handleIconClick}
          data-testid="creative-menu-button"
        >
          <MoreVert />
        </button>
        <Media query="(max-width: 800px)">
          {matches =>
            matches ? (
              <AdOptionsDrawer
                drawerOpen={isOpen()}
                hideDrawer={() => setAnchorEl(null)}
                {...commonProps}
              />
            ) : (
              <AdOptionsDropdown
                anchorEl={anchorEl}
                isOpen={isOpen()}
                hideMenu={hideMenu}
                {...commonProps}
              />
            )
          }
        </Media>
      </div>
    </MuiThemeProvider>
  );
};

CreativeOptions.propTypes = {
  creative: PropTypes.shape({
    id: PropTypes.number.isRequired
  }).isRequired,
  /* 'example' when it's used on the examples list, or 'demo' when
   * it's the user_creatives list */
  adType: PropTypes.string.isRequired,
  $state: PropTypes.object.isRequired,
  CreativeListService: PropTypes.object.isRequired,
  FlashService: PropTypes.object.isRequired,
  ShareableLinkService: PropTypes.object.isRequired
};

export default CreativeOptions;
